import React, {useState} from 'react';
import PropTypes from 'prop-types';

//MUI
import Box from '@mui/material/Box';

//UTILS
import {annualPlan} from '../../proptypes/annualPlan';
import {basemap} from '../../proptypes/basemap';
import {intervention} from '../../proptypes/intervention';
import {viewport} from '../../proptypes/viewport';
import {ROLES_LABEL} from '../../../config';
import {roles} from '../../proptypes/roles';
import {commoners} from '../../proptypes/commoners';
import {fileDetail} from '../../proptypes/fileDetail';

//BIKENTA
import AnnualPlanPanel from './AnnualPlanPanel';
import AlertDeleteDialog from '../../AlertDeleteDialog';
import DialogPanel from '../../DialogPanel';
import DocumentationPanel from './DocumentationPanel';
import ExecutionPanel from './ExecutionPanel/ExecutionPanel';
import PlanningPanel from './PlanningPanel';
import InterventionHeader from './InterventionHeader';
import {categoryFilters} from '../../proptypes/categoryFilters';
import {specie} from '../../proptypes/specie';
import {useTranslation} from 'react-i18next';

const InterventionMenu = ({
  annualPlansAssignedInSelectedIntervention,
  basemap,
  batches,
  categoryFilters,
  commoners,
  fileDetail,
  intervention,
  interventionZone,
  interventionZoneDetail,
  isInterventionMenuOpen,
  isOffline,
  multiselect,
  role,
  rolesList,
  selectedAnnualPlan,
  species,
  viewport,
  onCreateExecution,
  onAnnualPlanUpdateIntervention,
  onCreatePlantation,
  onCloseInterventionMenu,
  onEditEnd,
  onDelete,
  onDeleteExecution,
  onEditPlannedIntervention,
  onFileDetailOpen,
  onGetInterventionZoneSpecies,
  onUpdateExecution,
  onUpdateIntervention,
  onUploadFile,
  onDeleteFile,
  onDownloadFile,
  onInputInterventionZoneGeometryStarts,
  onInputInterventionZoneGeometryEnds,
  onNeverDo
}) => {

  const {t} = useTranslation();

  const [isExecutionEditable, setIsExecutionEditable] = useState(!intervention.done);
  const [isAlertDialogOpen, setIsAlertDialogOpen] = useState(false);

  /*EJECUCIÓN---------------------------------------------------------------------------------------------------------*/
  const handleExecutionEditChange = (isEditing) => setIsExecutionEditable(isEditing);

  /*DOCUMENTACIÓN-----------------------------------------------------------------------------------------------------*/
  const handleUploadFile = (uploadFormData) => onUploadFile(onUploadFile(intervention.intervention_zone, intervention.id, uploadFormData));
  const handleDeleteFile = fileId => onDeleteFile(intervention.intervention_zone, intervention.id, fileId);

  /*INTERVENCIÓN------------------------------------------------------------------------------------------------------*/
  const handleAlertDeleteIntervention = () => setIsAlertDialogOpen(true);
  const handleDeleteIntervention = () => {
    setIsAlertDialogOpen(false);
    onDelete(intervention.id);
  };
  const handleCancelDeleteIntervention = () => setIsAlertDialogOpen(false);
  const handleUpdateExecution = (intervention) => {
    onUpdateExecution(intervention);
    onCloseInterventionMenu();
  };
  return <>
    {
      isAlertDialogOpen ? <AlertDeleteDialog
        title={t('Eliminar intervención')}
        description={t('No se podrá recuperar la intervención. ¿Estás seguro?')}
        onCancel={handleCancelDeleteIntervention}
        onAccept={(interventionId) => handleDeleteIntervention(interventionId)}/> : 
        <DialogPanel
          isOpen={isInterventionMenuOpen}
          onClose={onEditEnd}
          label={t('DETALLES DE LA INTERVENCIÓN')}
          fullWidth
        >
          <Box sx={{p: 1.25}}>
            <InterventionHeader
              batches={batches}
              categoryFilters={categoryFilters}
              intervention={intervention}
              multiselect={multiselect}
              role={role}
              isOffline={isOffline}
              onAlertDeleteIntervention={handleAlertDeleteIntervention}
              onUpdateIntervention={(updatedIntervention) => {
                onUpdateIntervention(intervention.id, updatedIntervention);
              }}
            />
            <AnnualPlanPanel
              annualPlansAssignedInSelectedIntervention={annualPlansAssignedInSelectedIntervention}
              intervention={intervention}
              role={role}
              selectedAnnualPlan={selectedAnnualPlan}
              onAnnualPlanUpdateIntervention={onAnnualPlanUpdateIntervention}
            />
            <PlanningPanel
              isOffline={isOffline}
              species={species}
              intervention={intervention}
              interventionZone={interventionZone}
              role={role}
              onEditPlannedIntervention={onEditPlannedIntervention}
              onCreatePlantation={onCreatePlantation}
            />
            <ExecutionPanel
              basemap={basemap}
              batches={batches}
              isOffline={isOffline}
              isEditing={isExecutionEditable}
              intervention={intervention}
              interventionZone={interventionZone}
              interventionZoneDetail={interventionZoneDetail}
              role={role}
              viewport={viewport}
              onCreateExecution={onCreateExecution}
              onUpdateExecution={handleUpdateExecution}
              onEditChange={handleExecutionEditChange}
              /*onEditedAccept={handleEditedAccept}*/
              onDeleteExecution={onDeleteExecution}
              onGetInterventionZoneSpecies={onGetInterventionZoneSpecies}
              onInputInterventionZoneGeometryStarts={onInputInterventionZoneGeometryStarts}
              onInputInterventionZoneGeometryEnds={onInputInterventionZoneGeometryEnds}
              onNeverDo={onNeverDo}
            />
            <DocumentationPanel
              commoners={commoners}
              isOffline={isOffline}
              isEditing={isExecutionEditable}
              files={intervention.files}
              fileDetail={fileDetail}
              role={role}
              rolesList={rolesList}
              onEditChange={handleExecutionEditChange}
              onUploadFile={handleUploadFile}
              onDeleteFile={handleDeleteFile}
              onDownloadFile={onDownloadFile}
              onFileDetailOpen={onFileDetailOpen}
            />
          </Box>
        </DialogPanel>
    }
  </>;
};

InterventionMenu.propTypes = {
  annualPlansAssignedInSelectedIntervention: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.number,
    year: PropTypes.number
  })),
  basemap: basemap,
  batches: PropTypes.arrayOf(PropTypes.shape({id: PropTypes.number, name: PropTypes.string })),
  categoryFilters: categoryFilters,
  commoners: commoners,
  fileDetail: fileDetail,
  intervention: intervention,
  interventionZone: PropTypes.object,
  interventionZoneDetail: PropTypes.object,
  isOffline: PropTypes.bool,
  isInterventionMenuOpen: PropTypes.bool.isRequired,
  multiselect: PropTypes.bool,
  role: PropTypes.oneOf(Object.values(ROLES_LABEL)),
  rolesList: PropTypes.arrayOf(roles),
  species: PropTypes.arrayOf(specie),
  selectedAnnualPlan: annualPlan,
  viewport: viewport,
  onCreateExecution: PropTypes.func,
  onAnnualPlanUpdateIntervention: PropTypes.func,
  onCreatePlantation: PropTypes.func,
  onCloseInterventionMenu: PropTypes.func,
  onEditEnd: PropTypes.func,
  onDelete: PropTypes.func.isRequired,
  onEditPlannedIntervention: PropTypes.func.isRequired,
  onDeleteExecution: PropTypes.func,
  onFileDetailOpen: PropTypes.func,
  onUpdateIntervention: PropTypes.func,
  onUpdateExecution: PropTypes.func,
  onUploadFile: PropTypes.func.isRequired,
  onDeleteFile: PropTypes.func.isRequired,
  onDownloadFile: PropTypes.func.isRequired,
  onGetInterventionZoneSpecies: PropTypes.func,
  onInputInterventionZoneGeometryStarts: PropTypes.func,
  onInputInterventionZoneGeometryEnds: PropTypes.func,
  onNeverDo: PropTypes.func
};

export default InterventionMenu;
